
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import SearchModal from './SearchModal';
import EnhancedLoginModal from '../EnhancedLoginModal';
import './Header.css';
import logo from './logo.png';

const GOOGLE_CLIENT_ID = '172662423555-nffff5prjf0i75ujd3gp882dg7qvlh6q.apps.googleusercontent.com';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);

  const { getCartItemsCount, loadUserCart } = useCart();
  const { getWishlistItemsCount, loadUserWishlist } = useWishlist();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  // API data states
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isBrandsLoading, setIsBrandsLoading] = useState(true);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
  const [brandsError, setBrandsError] = useState(null);

  const [categoriesError, setCategoriesError] = useState(null);

  // your Django API eventually
  const websitePages = [
    { 
      type: 'page', 
      title: 'About King Solar Man', 
      url: '/about',
      description: 'Learn about our company and mission to provide solar energy solutions in Botswana',
      keywords: ['about', 'company', 'mission', 'vision', 'who we are']
    },
    { 
      type: 'page', 
      title: 'Contact Us', 
      url: '/contact',
      description: 'Get in touch with our solar specialists for consultations and support',
      keywords: ['contact', 'email', 'phone', 'support', 'help', 'get in touch']
    },
    { 
      type: 'page', 
      title: 'Solar Specialist Consultation', 
      url: '/specialist',
      description: 'Free solar consultation and custom system design for your home or business',
      keywords: ['specialist', 'consultation', 'design', 'quote', 'free consultation']
    },


    { 
      type: 'page', 
      title: 'Home - Solar Energy Solutions', 
      url: '/',
      description: 'Home page featuring our solar products and services',
      keywords: ['home', 'solar', 'energy', 'solutions', 'main page']
    }
  ];



  // Default fallback data if API fails - Updated to use API category IDs
  const defaultCategories = [
    { type: 'category', title: 'Solar Inverters', slug: '1', description: 'An Off-Grid Solar Inverter converts the direct current (DC) from solar panels into alternating current (AC) for use in homes or businesses that are not connected to the electrical grid' },
    { type: 'category', title: 'Solar Panels', slug: '3', description: 'Solar Panels for solar energy systems' },
    { type: 'category', title: 'Batteries', slug: '2', description: 'Batteries for solar energy systems' },
    { type: 'category', title: 'Accessories', slug: '4', description: 'Accessories for solar energy systems' },
    { type: 'category', title: 'solar_kits', slug: '6', description: 'Complete solar power system packages' },
    { type: 'category', title: 'mppt_controllers', slug: '5', description: 'MPPT charge controllers for solar energy systems' }
  ];

  const defaultBrands = [
    { type: 'brand', title: 'Deye', slug: 'deye', description: 'Premium hybrid solar inverters and energy solutions' },
    { type: 'brand', title: 'Dyness', slug: 'dyness', description: 'Lithium batteries and hybrid inverters for energy storage' },
    { type: 'brand', title: 'FreedomWon', slug: 'freedom-won', description: 'South African lithium battery solutions' },
    { type: 'brand', title: 'Fronius', slug: 'fronius', description: 'Austrian-made solar inverters known for reliability and efficiency' },
    { type: 'brand', title: 'Greenrich', slug: 'greenrich', description: 'Energy storage and management systems' },
    { type: 'brand', title: 'HinaESS', slug: 'hinaess', description: 'Energy storage solutions for residential and commercial applications' },
    { type: 'brand', title: 'Jinko', slug: 'jinko', description: 'World-leading solar panel manufacturer with high-efficiency modules' },
    { type: 'brand', title: 'Longi', slug: 'longi', description: 'Solar panels with advanced monocrystalline technology' },
    { type: 'brand', title: 'Luxpower', slug: 'luxpower', description: 'Hybrid inverters with advanced battery management' },
    { type: 'brand', title: 'Solis', slug: 'solis', description: 'Cost-effective solar inverters for residential and commercial use' },
    { type: 'brand', title: 'Sunwoda', slug: 'sunwoda', description: 'Energy storage and battery solutions' },
    { type: 'brand', title: 'Victron', slug: 'victron', description: 'Complete energy solutions including inverters, chargers, and monitoring' }
  ];

  // Use API data if available, fallback to default data
  const displayCategories = categoriesError ? defaultCategories : categories;
  const displayBrands = brandsError ? defaultBrands : brands;

  // API endpoints
  const API_BASE_URL = 'https://kingsolarman.pythonanywhere.com/api';

  // Fetch brands from API
  const fetchBrands = async () => {
    try {
      setIsBrandsLoading(true);
      setBrandsError(null);
      console.log('🔄 Fetching brands from API...');
      
      const response = await fetch(`${API_BASE_URL}/brands/`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Brands fetched successfully:', data);
      

      // Map API data to expected format
      const mappedBrands = data.results ? data.results.map(brand => ({
        type: 'brand',
        title: brand.name || brand.display_name || 'Unknown Brand',
        slug: brand.slug || brand.name?.toLowerCase() || 'unknown',
        description: brand.description || `${brand.name || brand.display_name} products`,
        id: brand.id,
        product_count: brand.product_count || 0
      })) : data.map(brand => ({
        type: 'brand',
        title: brand.name || brand.display_name || 'Unknown Brand',
        slug: brand.slug || brand.name?.toLowerCase() || 'unknown',
        description: brand.description || `${brand.name || brand.display_name} products`,
        id: brand.id,
        product_count: brand.product_count || 0
      }));
      
      setBrands(mappedBrands);
    } catch (error) {
      console.error('❌ Error fetching brands:', error);
      setBrandsError(error.message);
      // Keep default brands as fallback
    } finally {
      setIsBrandsLoading(false);
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setIsCategoriesLoading(true);
      setCategoriesError(null);
      console.log('🔄 Fetching categories from API...');
      
      const response = await fetch(`${API_BASE_URL}/categories/`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Categories fetched successfully:', data);
      
      // Map API data to expected format
      const mappedCategories = data.results ? data.results.map(category => ({
        type: 'category',
        title: category.display_name || category.name || category.title || 'Unknown Category',
        slug: category.slug || category.name?.toLowerCase() || 'unknown',
        description: category.description || `${category.display_name || category.name || category.title} products`
      })) : data.map(category => ({
        type: 'category',
        title: category.display_name || category.name || category.title || 'Unknown Category',
        slug: category.slug || category.name?.toLowerCase() || 'unknown',
        description: category.description || `${category.display_name || category.name || category.title} products`
      }));
      
      setCategories(mappedCategories);
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      setCategoriesError(error.message);
      // Keep default categories as fallback
    } finally {
      setIsCategoriesLoading(false);
    }
  };


  // Dynamic brand filtering functions
  const getBrandsByName = (namePatterns) => {
    return brands.filter(brand => 
      namePatterns.some(pattern => 
        brand.title.toLowerCase().includes(pattern.toLowerCase()) ||
        brand.slug.toLowerCase().includes(pattern.toLowerCase())
      )
    );
  };

  // Get Victron-related brands dynamically
  const getVictronBrands = () => {
    return getBrandsByName(['victron']);
  };

  // Get mounting system brands dynamically
  const getMountingBrands = () => {
    return getBrandsByName(['renusol', 'plas-sol', 'eco', 'mounting']);
  };

  // Get kit brands dynamically
  const getKitBrands = () => {
    return getBrandsByName(['solis', 'luxpower', 'kit']);
  };

  // Get solar panel brands dynamically
  const getSolarPanelBrands = () => {
    return getBrandsByName(['jinko', 'longi', 'solar panel']);
  };

  // Get battery brands dynamically  
  const getBatteryBrands = () => {
    return getBrandsByName(['battery', 'lithium', 'dyness', 'freedom']);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchBrands();
    fetchCategories();
  }, []);


  // Listen for user login events from the modal
  useEffect(() => {
    const handleUserLoggedIn = (event) => {
      const userData = event.detail;
      setUser(userData);
      
      // Load user's saved wishlist and cart when they log in
      if (userData.id) {
        console.log('Loading user wishlist and cart for user:', userData.id);
        loadUserWishlist(userData.id);
        loadUserCart(userData.id);
      }
    };

    const handleUserRegistered = (event) => {
      const userData = event.detail;
      setUser(userData);
      
      // Load user's saved wishlist and cart when they register
      if (userData.id) {
        console.log('Loading user wishlist and cart for new user:', userData.id);
        loadUserWishlist(userData.id);
        loadUserCart(userData.id);
      }
    };

    // Add event listeners
    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    window.addEventListener('userRegistered', handleUserRegistered);

    // Check localStorage for existing user
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        
        // Load user's saved wishlist and cart when they return to the site
        if (userData.id) {
          console.log('Restoring user wishlist and cart for returning user:', userData.id);
          loadUserWishlist(userData.id);
          loadUserCart(userData.id);
        }
      } catch (e) {
        console.error('Error parsing saved user:', e);
      }
    }

    // Cleanup event listeners
    return () => {
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
      window.removeEventListener('userRegistered', handleUserRegistered);
    };
  }, []);

  // Show logout confirmation modal
  const confirmLogout = () => {
    setShowLogoutConfirm(true);
  };

  // Handle actual logout
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    setShowLogoutConfirm(false);
    // Redirect to homepage (logged out from your site only)
    window.location.href = '/';
  };

  // Cancel logout
  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };


  // Real universal search function with improved error handling and fallback
  const performSearch = async (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    setSearchResults([]);
    const searchLower = term.toLowerCase();
    
    try {
      // 1. Try to search products from Django API with timeout
      let products = [];
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await fetch(`https://kingsolarman.pythonanywhere.com/api/products/?search=${encodeURIComponent(term)}`, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();

          products = data.results.map(product => ({
            type: 'product',
            id: product.id,
            title: product.name,
            category: typeof product.category === 'object' 
              ? (product.category.display_name || product.category.name || 'Unknown Category')
              : mapCategory(product.category),
            brand: typeof product.brand === 'object' 
              ? (product.brand.display_name || product.brand.name || 'Unknown Brand')
              : (typeof product.brand === 'string' ? product.brand : product.brand?.name || 'Unknown Brand'),
            price: product.current_price,
            image: product.primary_image,
            has_discount: product.has_discount,
            discount_percentage: product.discount_percentage,
            stock_status: product.stock_status,
            description: product.description || ''
          }));
          console.log(`✅ Found ${products.length} products from API`);
        } else {
          console.warn('⚠️ Product API returned error:', response.status);
        }
      } catch (error) {
        console.warn('⚠️ Product API unavailable, using fallback:', error.message);
      }

      // 2. Always search categories (use API data or fallback)
      const foundCategories = displayCategories.filter(cat => 
        cat.title.toLowerCase().includes(searchLower) ||
        cat.description.toLowerCase().includes(searchLower) ||
        cat.slug.toLowerCase().includes(searchLower)
      ).map(cat => ({ ...cat, type: 'category' }));

      // 3. Always search brands (use API data or fallback)
      const foundBrands = displayBrands.filter(brand => 
        brand.title.toLowerCase().includes(searchLower) ||
        brand.description.toLowerCase().includes(searchLower) ||
        brand.slug.toLowerCase().includes(searchLower)
      ).map(brand => ({ ...brand, type: 'brand' }));

      // 4. Always search website pages (always available locally)
      const foundPages = websitePages.filter(page => 
        page.title.toLowerCase().includes(searchLower) ||
        page.description.toLowerCase().includes(searchLower) ||
        page.keywords.some(keyword => keyword.toLowerCase().includes(searchLower))
      ).map(page => ({ ...page, type: 'page' }));

      // Combine all results (API products + local results)
      const allResults = [
        ...products,
        ...foundCategories,
        ...foundBrands,
        ...foundPages
      ];

      console.log(`🔍 Search results: ${allResults.length} total (${products.length} products, ${foundCategories.length} categories, ${foundBrands.length} brands, ${foundPages.length} pages)`);

      setSearchResults(allResults);

    } catch (error) {
      console.error('❌ Search error:', error);
      
      // Even if everything fails, provide local search results
      const fallbackResults = [
        ...displayCategories.filter(cat => 
          cat.title.toLowerCase().includes(searchLower)
        ).map(cat => ({ ...cat, type: 'category' })),
        ...displayBrands.filter(brand => 
          brand.title.toLowerCase().includes(searchLower)
        ).map(brand => ({ ...brand, type: 'brand' })),
        ...websitePages.filter(page => 
          page.title.toLowerCase().includes(searchLower)
        ).map(page => ({ ...page, type: 'page' }))
      ];
      
      console.log(`🔄 Using fallback search: ${fallbackResults.length} results`);
      setSearchResults(fallbackResults);
    } finally {
      setIsLoading(false);
    }
  };


  // Helper function to map API categories to display names
  const mapCategory = (apiCategory) => {
    const categoryMap = {
      'solar-inverters': 'Solar Inverters',
      'solar-panels': 'Solar Panels', 
      'batteries': 'Batteries',
      'accessories': 'Accessories',
      'solar_kits': 'solar_kits',
      'mppt_controllers': 'mppt_controllers'
    };
    return categoryMap[apiCategory] || apiCategory;
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Only search if there are at least 2 characters
    if (value.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 500);
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchClick = () => {
    if (searchTerm.trim()) {
      performSearch(searchTerm);
      setIsModalOpen(true);
    } else {
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      performSearch(searchTerm);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };


  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    inputRef.current?.focus();
  };


  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleProductsDropdown = () => {
    setIsProductsDropdownOpen(!isProductsDropdownOpen);
  };



  // Handle dropdown state management for both mobile and desktop
  useEffect(() => {
    // Reset dropdown when menu closes on mobile
    if (!isMenuOpen) {
      setIsDropdownOpen(false);
      setIsProductsDropdownOpen(false);
    }
  }, [isMenuOpen]);

  // Close dropdown when clicking outside (only on desktop)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('.nav-dropdown');
      const dropdownTrigger = document.querySelector('#nav-dropdown-trigger-part-2');
      
      // Only close dropdown on desktop (not when hamburger menu is open)
      const isMobile = window.innerWidth <= 768;
      const isMobileMenuOpen = isMenuOpen;
      
      // Don't close if clicking on dropdown trigger or dropdown content
      if ((dropdownTrigger && dropdownTrigger.contains(event.target)) ||
          (dropdown && dropdown.contains(event.target))) {
        return;
      }
      
      if (dropdown && !dropdown.contains(event.target) && isDropdownOpen && !isMobileMenuOpen) {
        setIsDropdownOpen(false);
      }
    };

    if (!isMenuOpen && (isDropdownOpen || isProductsDropdownOpen)) { // Only add listener when mobile menu is closed and dropdown is open
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isDropdownOpen, isProductsDropdownOpen, isMenuOpen]);

  // Close logout modal when clicking outside
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showLogoutConfirm) {
        cancelLogout();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showLogoutConfirm]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <header className="header">
        <div className="header-top">
          <div className="container">
            <div className="black-friday-banner">
              <span>🎁 Holiday Specials. December to January</span>
            </div>
          </div>
        </div>

        <div className="header-main">
          <div className="container">
            <div className="header-left">
              <div className="logo">
                <Link to="/">
                  <img 
                    src={logo} 
                    alt="Company Logo" 
                    className="logo-image"
                  />
                </Link>
              </div>
            </div>

            <div className="header-center">
              <div className="search-bar">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search solar products, brands, categories..."
                  value={searchTerm}
                  onChange={handleSearchInput}
                  onKeyPress={handleKeyPress}
                />
                {searchTerm && (
                  <button className="clear-btn" onClick={clearSearch}>×</button>
                )}
                <button className="search-btn" onClick={handleSearchClick}>
                  🔍
                </button>
              </div>
            </div>

            <div className="header-right">
              <div className={`header-actions ${user ? 'logged-in' : ''}`}>
                {user ? (
                  <>
                    <span className="user-greeting">
                      Hi, {user.name?.split(' ')[0] || user.email?.split('@')[0]}
                    </span>
                    <Link to="/account" className="account-btn">
                      My Account
                    </Link>
                    <button className="logout-btn" onClick={confirmLogout}>
                      Logout
                    </button>
                  </>

                ) : (
                  <button className="auth-btn" onClick={() => setShowLoginModal(true)}>
                    <span className="user-icon">👤</span>
                    Sign In
                  </button>
                )}
                

                
                <Link to="/wishlist" className="wishlist-btn">
                  ❤️
                  {getWishlistItemsCount() > 0 && (
                    <span className="wishlist-count">{getWishlistItemsCount()}</span>
                  )}
                </Link>
                
                <Link to="/cart" className="cart-btn">
                  🛒
                  {getCartItemsCount() > 0 && (
                    <span className="cart-count">{getCartItemsCount()}</span>
                  )}
                </Link>
                

                <button 
                  className="menu-toggle"
                  onClick={() => {
                    const newMenuState = !isMenuOpen;
                    setIsMenuOpen(newMenuState);
                    // Close dropdown when menu is closed
                    if (!newMenuState) {
                      setIsDropdownOpen(false);
                    }
                  }}
                >
                  ☰
                </button>
              </div>
            </div>
          </div>
        </div>

        <nav className={`main-nav ${isMenuOpen ? 'nav-open' : ''}`}>
          <div className="container">
            <div className="nav-links">
              <Link to="/">Home</Link>
              <Link to="/about">About Us</Link>



              <div className={`nav-dropdown ${isDropdownOpen ? 'open' : ''} ${isMenuOpen ? 'mobile-open' : ''}`}>
                <button 
                  id="nav-dropdown-trigger-part-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown();
                  }}
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="true"
                  onMouseEnter={() => {
                    // Only enable hover on desktop (not on mobile)
                    if (window.innerWidth > 768) {
                      setIsDropdownOpen(true);
                    }
                  }}
                  onMouseLeave={() => {
                    // Only enable hover on desktop (not on mobile)
                    if (window.innerWidth > 768) {
                      setIsDropdownOpen(false);
                    }
                  }}
                >
                  Product Menu {isDropdownOpen ? '▲' : '▼'}
                </button>
                <div className="dropdown-menu mega-menu">
                  <div className="mega-menu-content">

                    <div className="menu-column">
                      <h4>Products by Brand</h4>
                      {isBrandsLoading ? (
                        <div className="loading-text">Loading brands...</div>
                      ) : (
                        displayBrands.slice(0, 10).map(brand => (
                          <Link key={brand.slug} to={`/products?brand=${brand.slug}`}>
                            {brand.title}
                          </Link>
                        ))
                      )}
                      {brandsError && (
                        <div className="error-text">⚠️ Using default brands</div>
                      )}
                    </div>



                    <div className="menu-column">
                      <h4>Solar Inverters by Type</h4>
                      <Link to="/products?category=1">All Inverters</Link>
                      <Link to="/products?category=1&type=off-grid">Off Grid/Battery</Link>
                      <Link to="/products?category=1&type=hybrid">Hybrid Inverters</Link>
                      <Link to="/products?category=1&type=plug-play">Plug n' Play</Link>
                      <Link to="/products?category=1&type=string">String Inverters</Link>
                    </div>




                    <div className="menu-column">
                      <h4>Solar Panels</h4>
                      {isBrandsLoading ? (
                        <div className="loading-text">Loading solar panel brands...</div>
                      ) : (
                        <>
                          <Link to="/products?category=3">All Solar Panels</Link>
                          {getSolarPanelBrands().map(brand => (
                            <Link key={brand.slug} to={`/products?category=3&brand=${brand.slug}`}>
                              {brand.title}
                            </Link>
                          ))}
                        </>
                      )}
                      {brandsError && (
                        <div className="error-text">⚠️ Using fallback data</div>
                      )}
                    </div>


                    <div className="menu-column">
                      <h4>Batteries</h4>
                      {isBrandsLoading ? (
                        <div className="loading-text">Loading battery brands...</div>
                      ) : (
                        <>
                          <Link to="/products?category=2">All Batteries</Link>
                          <Link to="/products?category=2&type=lithium">Lithium</Link>
                          <Link to="/products?category=2&type=agm">AGM</Link>
                          <Link to="/products?category=2&type=gel">Gel</Link>
                          {getBatteryBrands().map(brand => (
                            <Link key={brand.slug} to={`/products?category=2&brand=${brand.slug}`}>
                              {brand.title}
                            </Link>
                          ))}
                          <Link to="/products?category=4&subcategory=battery-storage">Battery Storage & Mounting</Link>
                          <Link to="/products?category=4&subcategory=battery-cable">Battery Cable</Link>
                          <Link to="/products?category=4&subcategory=battery-fusing">Battery Fusing</Link>
                        </>
                      )}
                      {brandsError && (
                        <div className="error-text">⚠️ Using fallback data</div>
                      )}
                    </div>




                    <div className="menu-column">
                      <h4>Victron Energy</h4>
                      {isBrandsLoading ? (
                        <div className="loading-text">Loading Victron products...</div>
                      ) : (
                        <>
                          <Link to="/products?brand=victron">Victron All Products</Link>
                          <Link to="/products?brand=victron&category=6">Victron Solar Kits</Link>
                          <Link to="/products?brand=victron&category=1">Victron Inverters</Link>
                          <Link to="/products?brand=victron&category=5">Victron Charge Controllers</Link>
                          <Link to="/products?brand=victron&category=4">Victron Accessories</Link>
                        </>
                      )}
                      {brandsError && (
                        <div className="error-text">⚠️ Using fallback data</div>
                      )}
                    </div>


                    <div className="menu-column">
                      <h4>Solar Accessories</h4>
                      {isCategoriesLoading ? (
                        <div className="loading-text">Loading accessories...</div>
                      ) : (
                        <>
                          <Link to="/products?category=4">All Accessories</Link>
                          <Link to="/products?category=4&type=cables">Cables and Connectors</Link>
                          <Link to="/products?category=4&type=fuses">Fuses, Fuse Holders & DC Isolators</Link>
                          <Link to="/products?category=4&type=tools">Solar Tools</Link>
                          <Link to="/products?category=4&type=mounting">Mounting Hardware</Link>
                          <Link to="/products?category=4&type=monitoring">Monitoring Systems</Link>
                        </>
                      )}
                      {categoriesError && (
                        <div className="error-text">⚠️ Using fallback data</div>
                      )}
                    </div>


                    <div className="menu-column">
                      <h4>Solar Panel Mounting Systems</h4>
                      {isBrandsLoading ? (
                        <div className="loading-text">Loading mounting brands...</div>
                      ) : (
                        <>
                          <Link to="/products?category=accessories&type=mounting">All Mounting Systems</Link>
                          {getMountingBrands().map(brand => (
                            <Link key={brand.slug} to={`/products?brand=${brand.slug}&category=accessories&type=mounting`}>
                              {brand.title}
                            </Link>
                          ))}
                        </>
                      )}
                      {brandsError && (
                        <div className="error-text">⚠️ Using fallback data</div>
                      )}
                    </div>




                    <div className="menu-column">
                      <h4>Solar System Kits</h4>
                      {isBrandsLoading ? (
                        <div className="loading-text">Loading kit brands...</div>
                      ) : (
                        <>
                          <Link to="/products?category=6">All Kits</Link>
                          {getKitBrands().map(brand => (
                            <Link key={brand.slug} to={`/products?brand=${brand.slug}&category=6`}>
                              {brand.title} Kits
                            </Link>
                          ))}
                        </>
                      )}
                      {brandsError && (
                        <div className="error-text">⚠️ Using fallback data</div>
                      )}
                    </div>


                    <div className="menu-column">
                      <h4>MPPT Controllers</h4>
                      {isBrandsLoading ? (
                        <div className="loading-text">Loading MPPT brands...</div>
                      ) : (
                        <>
                          <Link to="/products?category=5">All MPPT Controllers</Link>
                          {getVictronBrands().map(brand => (
                            <Link key={brand.slug} to={`/products?brand=${brand.slug}&category=5`}>
                              {brand.title} MPPT
                            </Link>
                          ))}
                        </>
                      )}
                      {brandsError && (
                        <div className="error-text">⚠️ Using fallback data</div>
                      )}
                    </div>


                  </div>
                </div>
              </div>



              <Link to="/contact">Contact Us</Link>
              <Link to="/track-order">Track Order</Link>
              <Link to="/specialist">Specialist</Link>


            </div>
          </div>
        </nav>
      </header>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="logout-confirm-modal">
          <div className="logout-modal-content">
            <div className="logout-modal-header">
              <h3>Confirm Logout</h3>
              <button className="logout-modal-close" onClick={cancelLogout}>
                ×
              </button>
            </div>
            <div className="logout-modal-body">
              <p>Are you sure you want to log out?</p>
              <div className="logout-modal-actions">
                <button 
                  className="logout-modal-cancel"
                  onClick={cancelLogout}
                >
                  Cancel
                </button>
                <button 
                  className="logout-modal-confirm"
                  onClick={handleLogout}
                >
                  Yes, Logout
                </button>
              </div>
            </div>
          </div>
          <div className="logout-modal-overlay" onClick={cancelLogout}></div>
        </div>
      )}


      <SearchModal
        isOpen={isModalOpen}
        onClose={closeModal}
        searchTerm={searchTerm}
        results={searchResults}
        isLoading={isLoading}
      />
      
      {/* Enhanced Login Modal */}
      <EnhancedLoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        title="Welcome to KingSolarMan"
        subtitle="Join us to unlock these amazing benefits:"
        benefits={[
          { icon: "🛒", text: "Save your cart across devices" },
          { icon: "❤️", text: "Keep your wishlist forever" },
          { icon: "📋", text: "Track your order history" },
          { icon: "🎯", text: "Quick reordering of favorites" },
          { icon: "💰", text: "Exclusive member discounts" },
          { icon: "🚚", text: "Priority customer support" }
        ]}
        isMandatory={false}
      />
    </>
  );
};

export default Header;
